import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StaticPage } from '../types';
import {
  fetchAllPages,
  createPage,
  updatePage,
  deletePage,
} from '../utils/pageApi';

const AddHtmlPage: React.FC = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState<StaticPage[]>([]);
  const [editingPage, setEditingPage] = useState<StaticPage | null>(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('adminAuth') !== '1') {
      navigate('/admin-login');
      return;
    }
    loadPages();
  }, [navigate]);

  const loadPages = async () => {
    const result = await fetchAllPages();
    if (result.success && result.data) {
      setPages(result.data);
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to load pages' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const resetForm = () => {
    setTitle('');
    setSlug('');
    setContent('');
    setIsPublished(false);
    setEditingPage(null);
    setShowForm(false);
  };

  const handleEdit = (page: StaticPage) => {
    setEditingPage(page);
    setTitle(page.title);
    setSlug(page.slug);
    setContent(page.content);
    setIsPublished(page.is_published);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDownload = (page: StaticPage) => {
    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${page.title}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 800px; margin: 0 auto; padding: 24px; line-height: 1.6; color: #333; }
    h1 { font-size: 2rem; font-weight: 800; margin-bottom: 24px; }
    a { color: #2563eb; }
    img { max-width: 100%; height: auto; }
    table { border-collapse: collapse; width: 100%; margin: 16px 0; }
    th, td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; }
    th { background: #f5f5f5; font-weight: 600; }
    blockquote { border-left: 4px solid #2563eb; padding: 8px 16px; margin: 16px 0; color: #555; background: #f9fafb; }
    code { background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: monospace; }
    pre { background: #1f2937; color: #f9fafb; padding: 16px; border-radius: 8px; overflow-x: auto; }
    pre code { background: none; padding: 0; color: inherit; }
  </style>
</head>
<body>
  <h1>${page.title}</h1>
  ${page.content}
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${page.slug}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this page?')) {
      setLoading(true);
      const result = await deletePage(id);
      if (result.success) {
        loadPages();
        setMessage({ type: 'success', text: 'Page deleted successfully!' });
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to delete page' });
      }
      setLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title || !slug || !content) {
      setMessage({ type: 'error', text: 'Please fill in all required fields.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setLoading(true);
    
    const pageData = {
      title,
      slug,
      content,
      is_published: isPublished,
    };

    let result;
    if (editingPage) {
      result = await updatePage(editingPage.id, pageData);
    } else {
      result = await createPage(pageData);
    }

    if (result.success) {
      loadPages();
      resetForm();
      setMessage({ type: 'success', text: editingPage ? 'Page updated successfully!' : 'Page created successfully!' });
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to save page' });
    }
    
    setLoading(false);
    setTimeout(() => setMessage(null), 3000);
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-100)', padding: '26px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--gray-800)' }}>📄 Static HTML Pages</h1>
          <button
            onClick={() => navigate('/admin')}
            className="btn btn-outline-gray btn-sm"
          >
            Back to Admin
          </button>
        </div>

        {message && (
          <div style={{
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
            color: message.type === 'success' ? '#166534' : '#991b1b'
          }}>
            {message.text}
          </div>
        )}

        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn btn-primary btn-sm"
            disabled={loading}
          >
            {showForm ? 'Cancel' : 'Add New Page'}
          </button>
        </div>

        {showForm && (
          <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '16px' }}>{editingPage ? 'Edit Page' : 'Add New Page'}</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (!editingPage) setSlug(generateSlug(e.target.value));
                  }}
                  style={{ width: '100%', padding: '8px 12px', border: '1.5px solid var(--gray-200)', borderRadius: '8px', fontSize: '0.95rem' }}
                  required
                  disabled={loading}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Slug *</label>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1.5px solid var(--gray-200)', borderRadius: '8px', fontSize: '0.95rem' }}
                  required
                  disabled={loading}
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '4px' }}>URL: /page/{slug}</p>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Content (HTML) *</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1.5px solid var(--gray-200)', borderRadius: '8px', fontSize: '0.85rem', fontFamily: 'monospace' }}
                  rows={10}
                  required
                  disabled={loading}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                    style={{ marginRight: '8px' }}
                    disabled={loading}
                  />
                  <span>Published</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (editingPage ? 'Update Page' : 'Create Page')}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn btn-outline-gray btn-sm"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, padding: '16px 20px', borderBottom: '1px solid var(--gray-200)' }}>All Pages</h2>
          {pages.length === 0 ? (
            <p style={{ padding: '16px 20px', color: 'var(--gray-400)' }}>No pages created yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: 'var(--gray-50)' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-600)' }}>Title</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-600)' }}>Slug</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-600)' }}>Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-600)' }}>Created</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-600)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map((page) => (
                    <tr key={page.id} style={{ borderTop: '1px solid var(--gray-200)' }}>
                      <td style={{ padding: '12px 16px' }}>{page.title}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <a
                          href={`/page/${page.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--blue)', textDecoration: 'none' }}
                        >
                          /page/{page.slug}
                        </a>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          padding: '2px 10px',
                          fontSize: '0.75rem',
                          borderRadius: '50px',
                          background: page.is_published ? 'var(--green-pale)' : 'var(--gray-100)',
                          color: page.is_published ? '#166534' : 'var(--gray-600)'
                        }}>
                          {page.is_published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--gray-500)' }}>{page.created_at ? new Date(page.created_at).toLocaleDateString() : '-'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <button
                          onClick={() => handleEdit(page)}
                          style={{ color: 'var(--blue)', background: 'none', border: 'none', cursor: 'pointer', marginRight: '8px' }}
                          disabled={loading}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDownload(page)}
                          style={{ color: 'var(--green)', background: 'none', border: 'none', cursor: 'pointer', marginRight: '8px' }}
                          disabled={loading}
                          title="Download as HTML"
                        >
                          ⬇ Download
                        </button>
                        <button
                          onClick={() => handleDelete(page.id)}
                          style={{ color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer' }}
                          disabled={loading}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddHtmlPage;